import {RecyclingStoreTopListProps, RecyclingStoreTopListValues} from "./types";
import {useTranslation} from "react-i18next";
import React, {ChangeEvent, useEffect, useState} from "react";
import InputSearch from "@sharedComponents/InputSearch";
import InputSelect from "@sharedComponents/InputSelect";
import LoadingIndicator from "@sharedComponents/LoadingIndicator";
import {IndicatorTheme} from "@sharedComponents/LoadingIndicator/types";
import {InputSelectOption, InputSelectTheme} from "@sharedComponents/InputSelect/types";
import {County} from "@apps/Shared/Types/County";
import FormsService from "@apps/Shared/Services/Forms/Forms.service";
import {StatusCode} from "@apps/Shared/Services/Response.types";

/**
 * Builds the county dropdown from the County enum from AssociationBattle/types.
 * `All`, "Hela Sverige" is always the first option, the rest are sorted alphabetically.
 */
const countyOptions: InputSelectOption[] = [
    County.All,
    ...Object.values(County)
        .filter(county => county !== County.All)
        .sort((a, b) => a.localeCompare(b)),
].map(county => ({
    value: county,
    label: county,
}));

/** Build the ranked list one store at a time.
 * A tied store reuses the already calculated position of the previous store,
 * otherwise its array index is its rank.
 */
const assignRanking = (nominatedStores: RecyclingStoreTopListValues[]) =>
    nominatedStores.reduce<RecyclingStoreTopListValues[]>((rankedStores, store, index) => {
        const previousStore = rankedStores[index - 1];
        const isTie = previousStore &&
            store.rating === previousStore.rating &&
            store.ratingCount === previousStore.ratingCount;

        rankedStores.push({
            ...store,
            position: isTie
                ? previousStore.position
                : index + 1,
        });

        return rankedStores;
    }, []);

const RecyclingStoreTopList = (props: RecyclingStoreTopListProps) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [allNominatedStores, setAllNominatedStores] = useState<RecyclingStoreTopListValues[]>([]);
    const [search, setSearch] = useState<string>("");
    const [selectedCounty, setSelectedCounty] = useState<County>(County.All);
    const [showAllStores, setShowAllStores] = useState(false);
    const [error, setError] = useState<string>("");
    const selectedCountyOption = countyOptions.find(option => option.value === selectedCounty);

    const filteredStores = filterTopList(
        allNominatedStores,
        selectedCounty,
        search
    );

    const visibleStores = showAllStores
        ? filteredStores
        : filteredStores.slice(0, 10);

    const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const onCountyChange = (option: InputSelectOption) => {
        setSelectedCounty(option.value as County);
    };

    const handleFetchError = () => {
        setAllNominatedStores([]);
        setError("Failed to fetch items.");
    };

    const fetchAllNominatedStores = async () => {
        setError("");

        try {
            const result = await FormsService.GetSiteNominationLeaderboard();

            if (result?.status !== StatusCode.OK) {
                handleFetchError();
                return;
            }

            const responseData = result.data;

            const allNominatedStores = typeof responseData === "string"
                ? JSON.parse(responseData) as RecyclingStoreTopListValues[]
                : responseData;

            if (!Array.isArray(allNominatedStores)) {
                handleFetchError();
                return;
            }
            const nominatedStores = assignRanking(allNominatedStores);

            setAllNominatedStores(nominatedStores);
        } catch {
            handleFetchError();
        }
    };

    useEffect(function initialize() {
        setLoading(true);
        fetchAllNominatedStores().finally(() => {
            setLoading(false);
        });
    }, []);

    function filterTopList(
        stores: RecyclingStoreTopListValues[],
        county: County = County.All,
        search: string = ""
    ): RecyclingStoreTopListValues[] {
        const searchTerm = search.trim().toLowerCase();
        const countyLower = county.toLowerCase();

        // National list by default, county if selected in dropdown
        const storesInSelectedCounty = county === County.All
            ? stores
            : stores.filter(store =>
                store.county.toLowerCase().includes(countyLower)
            );

        // If filtered by county, assign stores in that county with a county-specific ranking.
        const rankedStores = county === County.All
            ? storesInSelectedCounty
            : assignRanking(storesInSelectedCounty);

        // Filter by search
        const matchingStores = searchTerm
            ? rankedStores.filter(store =>
                store.siteName.toLowerCase().includes(searchTerm) ||
                store.county.toLowerCase().includes(searchTerm) ||
                store.municipality.toLowerCase().includes(searchTerm) ||
                store.city.toLowerCase().includes(searchTerm)
            )
            : rankedStores;

        return matchingStores;
    }

    // Replace decimal points with commas for display
    const ratingFormatter = new Intl.NumberFormat("sv-SE", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    })

    return (
        <>
            {loading ? (
                    <LoadingIndicator customMargins="5rem 0" theme={IndicatorTheme.DarkMode}/>
                ) :
                (
                    <div className="recycling-store-top-list">
                        <div className="recycling-store-top-list__heading">
                            {props.heading && (
                                <h1>
                                    {props.heading}
                                </h1>
                            )}
                        </div>

                        {props.description && (
                            <div
                                className="recycling-store-top-list__description"
                                dangerouslySetInnerHTML={{__html: props.description}}
                            />
                        )}

                        <div className="recycling-store-top-list__filters">

                            <div className="recycling-store-top-list__county-select">

                                <InputSelect
                                    id="recycling-store-top-list-county-selector"
                                    label=""
                                    allowInput={false}
                                    hideLabel={true}
                                    onChange={onCountyChange}
                                    value={selectedCountyOption}
                                    theme={InputSelectTheme.Default}
                                    options={countyOptions}
                                    placeholder={t("campaign.selectCountyPlaceholder")}
                                />
                            </div>

                            <InputSearch
                                id="recycling-store-top-list-search"
                                autoComplete="off"
                                label=""
                                value={search}
                                onChange={onSearchChange}
                                placeholder={t("campaign.searchPlaceholder")}
                            />
                        </div>

                        {error && (
                            <div className="recycling-store-top-list__error">
                                <p>{t("error.somethingwrong")}</p>
                            </div>
                        )}

                        {!error && filteredStores.length === 0 && (
                            <div className="recycling-store-top-list__no-result">
                                <span className="recycling-store-top-list__no-result__text">
                                    <p>{t("campaign.noResults")}</p>
                                </span>
                            </div>
                        )}

                        {!error && filteredStores.length > 0 && (
                            <div className="recycling-store-top-list__list">
                                <div className="recycling-store-top-list__headers">
                                    <h3>{t("campaign.station")}</h3>
                                    <h3>{t("campaign.rating")}</h3>
                                </div>
                                {visibleStores.map(item => (
                                    <div
                                        key={item.customerId}
                                        className="recycling-store-top-list__item"
                                    >
                                        <span className="recycling-store-top-list__item-store">
                                            <span className="recycling-store-top-list__item-position">
                                                {item.position}.
                                            </span>
                                            <span className="recycling-store-top-list__item-name">
                                                {item.siteName}
                                            </span>
                                        </span>

                                        <span className="recycling-store-top-list__item-rating">
                                            <span
                                                className="recycling-store-top-list__item-rating-value">{ratingFormatter.format(item.rating)}</span>
                                            <span className="recycling-store-top-list__item-rating-max"> / 5</span>
                                        </span>
                                    </div>
                                ))}
                                {filteredStores.length > 10 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllStores(!showAllStores)}
                                        className="recycling-store-top-list__button button button--primary-alt"
                                    >
                                        <div className="button__content">
                                            {showAllStores ? t('campaign.show10') : t('campaign.showAll')}
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
        </>
    );
};

export default RecyclingStoreTopList;
